import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useIntl } from "react-intl";
import { CheckboxWembii } from "../../CheckboxWembii";
import { BoardType } from "../../../../cores/boards/constants/board-type.constant";
import {
  MinimizedViewConfig,
  MinimizedViewField,
} from "../../../../cores/boards/constants/minimized-view-fields";

interface Props {
  isOpen: boolean;
  boardType?: string;
  initialConfig: MinimizedViewConfig;
  confirmLabelId: string;
  onCancel: () => void;
  onConfirm: (config: MinimizedViewConfig) => void;
}

const FIELD_LABELS: { key: MinimizedViewField; id: string; businessOnly?: boolean }[] = [
  { key: "title", id: "app.crmBusiness.minimizedView.title" },
  { key: "tags", id: "app.crmBusiness.minimizedView.tags" },
  { key: "value", id: "app.crmBusiness.minimizedView.value", businessOnly: true },
  {
    key: "closeDate",
    id: "app.crmBusiness.minimizedView.closeDate",
    businessOnly: true,
  },
  { key: "contacts", id: "app.crmBusiness.minimizedView.contacts" },
  { key: "assignee", id: "app.crmBusiness.minimizedView.assignee" },
  { key: "inactivity", id: "app.crmBusiness.minimizedView.inactivity" },
];

export default function MinimizeViewConfig({
  isOpen,
  boardType,
  initialConfig,
  confirmLabelId,
  onCancel,
  onConfirm,
}: Props) {
  const intl = useIntl();
  const [config, setConfig] = useState<MinimizedViewConfig>(initialConfig);

  const fields = useMemo(
    () =>
      FIELD_LABELS.filter(
        (field) =>
          !field.businessOnly || boardType === BoardType.BUSINESS
      ),
    [boardType]
  );

  const toggleField = useCallback((key: MinimizedViewField, value: boolean) => {
    if (key === "title") return;
    setConfig((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleOpened = useCallback(() => {
    setConfig(initialConfig);
  }, [initialConfig]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={onCancel}
      onOpened={handleOpened}
      centered
      zIndex={2000}
    >
      <ModalHeader toggle={onCancel}>
        {intl.formatMessage({ id: "app.crmBusiness.minimizedView.titleModal" })}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3" style={{ fontSize: 14 }}>
          {intl.formatMessage({ id: "app.crmBusiness.minimizedView.hint" })}
        </p>
        {fields.map((field) => (
          <CheckboxWembii
            key={field.key}
            id={`minimized-view-${field.key}`}
            label={intl.formatMessage({ id: field.id })}
            checked={!!config[field.key]}
            disabled={field.key === "title"}
            onChange={(value) => toggleField(field.key, value)}
          />
        ))}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onCancel}>
          {intl.formatMessage({ id: "app.crmBusiness.minimizedView.cancel" })}
        </Button>
        <Button color="primary" onClick={() => onConfirm({ ...config, title: true })}>
          {intl.formatMessage({ id: confirmLabelId })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
